import type { Locale } from '@/config/locales';
import type { JoinApprovalLabels } from '@/components/session/JoinApprovalPanel';

export interface ComensalSessionLabels {
  title: string;
  browseOnly: string;
  scanning: string;
  nameLabel: string;
  namePlaceholder: string;
  saveName: string;
  codeLabel: string;
  codePlaceholder: string;
  join: string;
  pendingHost: string;
  pendingCode: string;
  rejected: string;
  retry: string;
  cartBlocked: string;
  ledgerCartBlocked: string;
  approval: JoinApprovalLabels;
}

const es: ComensalSessionLabels = {
  title: 'Únete a esta mesa',
  browseOnly: 'Puedes ver el menú. Para agregar productos y ordenar, primero debes unirte a la mesa.',
  scanning: 'Recuperando tu lugar en la mesa…',
  nameLabel: 'Tu nombre',
  namePlaceholder: 'Nombre para la mesa',
  saveName: 'Guardar nombre',
  codeLabel: 'Código de 4 dígitos',
  codePlaceholder: '0000',
  join: 'Unirme',
  pendingHost: 'Tu solicitud está esperando la aprobación de quien abrió la mesa. Si pasan unos 90 segundos, se solicitará ayuda al personal.',
  pendingCode: 'Introduce el código de la mesa para unirte.',
  rejected: 'La solicitud fue rechazada. Pide ayuda al personal.',
  retry: 'Intentar de nuevo',
  cartBlocked: 'Únete a la mesa antes de agregar productos.',
  ledgerCartBlocked: 'La división de cuentas está pausada. Pide ayuda al personal antes de agregar productos.',
  approval: {
    title: 'Solicitudes para unirse',
    question: '¿Esta persona pagará por separado?',
    covered: 'Yo cubro',
    coveredHint: 'Se suma a mi cuenta',
    split: 'Por separado',
    splitHint: 'Crea su propia cuenta',
    approve: 'Aprobar',
    reject: 'Rechazar',
  },
};

const ko: ComensalSessionLabels = {
  title: '테이블에 합류하기',
  browseOnly: '메뉴는 볼 수 있습니다. 장바구니와 주문은 테이블 합류 후 이용해 주세요.',
  scanning: '테이블의 내 자리를 복구하는 중…',
  nameLabel: '이름',
  namePlaceholder: '테이블에 표시할 이름',
  saveName: '이름 저장',
  codeLabel: '4자리 합류 코드',
  codePlaceholder: '0000',
  join: '합류하기',
  pendingHost: '테이블 대표의 승인을 기다리고 있습니다. 약 90초가 지나면 직원에게 도움을 요청합니다.',
  pendingCode: '테이블의 4자리 코드를 입력해 합류하세요.',
  rejected: '합류 요청이 거절되었습니다. 직원에게 도움을 요청해 주세요.',
  retry: '다시 시도',
  cartBlocked: '상품을 담기 전에 테이블에 합류해 주세요.',
  ledgerCartBlocked: '분할 결제가 중지되었습니다. 상품을 담기 전에 직원에게 문의해 주세요.',
  approval: {
    title: '합류 요청',
    question: '이 손님은 따로 결제하나요?',
    covered: '내가 함께 결제',
    coveredHint: '대표 계산서에 합산',
    split: '따로 결제',
    splitHint: '개별 계산서 생성',
    approve: '승인',
    reject: '거절',
  },
};

const en: ComensalSessionLabels = {
  title: 'Join this table',
  browseOnly: 'You can browse the menu. Join the table before adding items or ordering.',
  scanning: 'Restoring your place at the table…',
  nameLabel: 'Your name',
  namePlaceholder: 'Name shown at the table',
  saveName: 'Save name',
  codeLabel: '4-digit table code',
  codePlaceholder: '0000',
  join: 'Join table',
  pendingHost: 'Your request is waiting for approval from the table host. After about 90 seconds, staff assistance will be requested.',
  pendingCode: 'Enter the 4-digit table code to join.',
  rejected: 'Your request was rejected. Please ask a staff member for help.',
  retry: 'Try again',
  cartBlocked: 'Join the table before adding items.',
  ledgerCartBlocked: 'Split payment is paused. Ask a staff member before adding items.',
  approval: {
    title: 'Requests to join',
    question: 'Will this person pay separately?',
    covered: 'I will cover',
    coveredHint: 'Add to my bill',
    split: 'Separate bill',
    splitHint: 'Create their own bill',
    approve: 'Approve',
    reject: 'Reject',
  },
};

export function getComensalSessionLabels(locale: Locale | null | undefined): ComensalSessionLabels {
  if (locale === 'es') return es;
  if (locale === 'ko') return ko;
  return en;
}
